import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import AnalysisSummaryHeader from './components/AnalysisSummaryHeader';
import AnalysisFilters from './components/AnalysisFilters';
import AnalysisTabNavigation from './components/AnalysisTabNavigation';
import AnalysisFindingCard from './components/AnalysisFindingCard';
import NaturalLanguageQuery from './components/NaturalLanguageQuery';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { aiAnalysisService } from '../../services/aiAnalysisService';

const AIAnalysisDashboard = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showQueryPanel, setShowQueryPanel] = useState(false);

  // New state for document base selection
  const [selectedDocumentBase, setSelectedDocumentBase] = useState('all');
  const [documentBaseOptions, setDocumentBaseOptions] = useState([]);

  // Real data state instead of mock data
  const [analysisData, setAnalysisData] = useState({
    documentsAnalyzed: 0,
    categories: [],
    allFindings: [],
    documentOptions: [{ value: 'all', label: 'All Documents' }]
  });

  // Filter and tab state
  const [filters, setFilters] = useState({
    document: 'all',
    issueType: 'all',
    severity: 'all',
    status: 'all',
    searchQuery: '',
    sortBy: 'severity_desc'
  });

  // Load document bases and analysis data on component mount
  useEffect(() => {
    loadDocumentBases();
  }, []);

  // Load analysis data when selectedDocumentBase changes
  useEffect(() => {
    loadAnalysisData(selectedDocumentBase);
  }, [selectedDocumentBase]);

  const loadDocumentBases = async () => {
    try {
      const documentBases = await aiAnalysisService?.getDocumentBases();
      const options = [
        { value: 'all', label: 'All Document Bases' },
        ...documentBases?.map(base => ({
          value: base?.id,
          label: base?.name
        }))
      ];
      setDocumentBaseOptions(options);
    } catch (error) {
      console.error('Error loading document bases:', error);
      setDocumentBaseOptions([{ value: 'all', label: 'All Document Bases' }]);
    }
  };

  const loadAnalysisData = async (documentBaseId = 'all') => {
    try {
      setIsLoading(true);
      const data = await aiAnalysisService?.getAnalysisData(documentBaseId);
      setAnalysisData(data);
    } catch (error) {
      console.error('Error loading analysis data:', error);
      // Set empty state on error
      setAnalysisData({
        documentsAnalyzed: 0,
        categories: [
          { type: 'gap_detection', count: 0, resolved: 0, severity: 'high', icon: 'Search' },
          { type: 'inconsistencies', count: 0, resolved: 0, severity: 'medium', icon: 'AlertTriangle' },
          { type: 'contradictions', count: 0, resolved: 0, severity: 'critical', icon: 'XCircle' },
          { type: 'grammar_issues', count: 0, resolved: 0, severity: 'low', icon: 'Type' },
          { type: 'spelling_errors', count: 0, resolved: 0, severity: 'low', icon: 'Spellcheck' },
          { type: 'language_issues', count: 0, resolved: 0, severity: 'medium', icon: 'Globe' }
        ],
        allFindings: [],
        documentOptions: [{ value: 'all', label: 'All Documents' }]
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter findings based on active tab and filters
  const getFilteredFindings = () => {
    let filtered = [...analysisData?.allFindings];

    // Filter by tab
    if (activeTab !== 'all') {
      if (activeTab === 'resolved') {
        filtered = filtered?.filter(f => f?.status === 'resolved');
      } else if (activeTab === 'gaps') {
        filtered = filtered?.filter(f => f?.type === 'gap_detection');
      } else if (activeTab === 'inconsistencies') {
        filtered = filtered?.filter(f => f?.type === 'inconsistencies');
      } else if (activeTab === 'contradictions') {
        filtered = filtered?.filter(f => f?.type === 'contradictions');
      } else if (activeTab === 'language') {
        filtered = filtered?.filter(f => ['grammar_issues', 'spelling_errors', 'language_issues']?.includes(f?.type));
      }
    }

    // Apply filters
    if (filters?.document !== 'all') {
      filtered = filtered?.filter(f => f?.documentId === filters?.document);
    }
    if (filters?.issueType !== 'all') {
      filtered = filtered?.filter(f => f?.type === filters?.issueType);
    }
    if (filters?.severity !== 'all') {
      filtered = filtered?.filter(f => f?.severity === filters?.severity);
    }
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(f => f?.status === filters?.status);
    }
    if (filters?.searchQuery?.trim()) {
      const query = filters?.searchQuery?.toLowerCase();
      filtered = filtered?.filter(f => 
        f?.title?.toLowerCase()?.includes(query) ||
        f?.description?.toLowerCase()?.includes(query) ||
        f?.documentName?.toLowerCase()?.includes(query)
      );
    }

    // Sort findings
    filtered?.sort((a, b) => {
      switch (filters?.sortBy) {
        case 'severity_desc':
          const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
          return severityOrder?.[b?.severity] - severityOrder?.[a?.severity];
        case 'severity_asc':
          const severityOrderAsc = { critical: 4, high: 3, medium: 2, low: 1 };
          return severityOrderAsc?.[a?.severity] - severityOrderAsc?.[b?.severity];
        case 'date_desc':
          return new Date(b.detectedAt) - new Date(a.detectedAt);
        case 'date_asc':
          return new Date(a.detectedAt) - new Date(b.detectedAt);
        case 'document':
          return a?.documentName?.localeCompare(b?.documentName);
        case 'type':
          return a?.type?.localeCompare(b?.type);
        default:
          return 0;
      }
    });

    return filtered;
  };

  // Calculate tab counts
  const getTabCounts = () => {
    return {
      all: analysisData?.allFindings?.length,
      gaps: analysisData?.allFindings?.filter(f => f?.type === 'gap_detection')?.length,
      inconsistencies: analysisData?.allFindings?.filter(f => f?.type === 'inconsistencies')?.length,
      contradictions: analysisData?.allFindings?.filter(f => f?.type === 'contradictions')?.length,
      language: analysisData?.allFindings?.filter(f => ['grammar_issues', 'spelling_errors', 'language_issues']?.includes(f?.type))?.length,
      resolved: analysisData?.allFindings?.filter(f => f?.status === 'resolved')?.length
    };
  };

  const filteredFindings = getFilteredFindings();
  const tabCounts = getTabCounts();

  // Event handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      document: 'all',
      issueType: 'all',
      severity: 'all',
      status: 'all',
      searchQuery: '',
      sortBy: 'severity_desc'
    });
  };

  const handleBulkAction = (action) => {
    switch (action) {
      case 'accept_all': console.log('Accepting all suggestions...');
        break;
      case 'create_change_requests': navigate('/change-management');
        break;
      case 'mark_reviewed': console.log('Marking as reviewed...');
        break;
      case 'export_report':
        console.log('Exporting analysis report...');
        break;
      default:
        break;
    }
  };

  const handleAcceptSuggestion = (findingId) => {
    console.log('Accepting suggestion for finding:', findingId);
  };

  const handleCreateChangeRequest = (finding) => {
    navigate('/change-management', { 
      state: { 
        fromAnalysis: true, 
        finding: finding 
      } 
    });
  };

  const handleNavigateToDocument = (documentId, location) => {
    navigate('/document-editor', { 
      state: { 
        documentId: documentId, 
        highlightLocation: location 
      } 
    });
  };

  // Updated refresh function to reload real data
  const handleRefreshAnalysis = async () => {
    await loadAnalysisData(selectedDocumentBase);
  };

  const handleNaturalLanguageQuery = async (query, documentBaseId = null) => {
    try {
      // Use the passed documentBaseId or fall back to selected document base
      const targetDocumentBase = documentBaseId || selectedDocumentBase;
      return await aiAnalysisService?.processNaturalLanguageQuery(query, analysisData, targetDocumentBase);
    } catch (error) {
      console.error('Error processing query:', error);
      return 'I encountered an error while processing your query. Please try asking about specific aspects of the document analysis.';
    }
  };

  // Get current document base name for display
  const getCurrentDocumentBaseName = () => {
    if (selectedDocumentBase === 'all') return 'All Document Bases';
    const selectedBase = documentBaseOptions?.find(option => option?.value === selectedDocumentBase);
    return selectedBase?.label || 'Selected Document Base';
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
          isMenuOpen={isSidebarOpen}
        />
        <Sidebar 
          isCollapsed={isSidebarCollapsed}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        <main className={`pt-16 transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        }`}>
          <div className="p-6">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Icon name="Loader2" size={48} className="text-primary mx-auto mb-4 animate-spin" />
                <h3 className="text-lg font-medium text-foreground mb-2">Loading Analysis Data</h3>
                <p className="text-muted-foreground">
                  Analyzing documents and generating insights...
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        isMenuOpen={isSidebarOpen}
      />
      <Sidebar 
        isCollapsed={isSidebarCollapsed}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />
      <main className={`pt-16 transition-all duration-300 ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="p-6">
          {/* Document Base Selection */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Icon name="Database" size={20} className="text-primary" />
                  <h2 className="text-lg font-medium text-foreground">Document Base Analysis</h2>
                </div>
                <div className="w-64">
                  <Select
                    placeholder="Select document base"
                    options={documentBaseOptions}
                    value={selectedDocumentBase}
                    onChange={setSelectedDocumentBase}
                  />
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                Currently analyzing: <span className="font-medium text-foreground">{getCurrentDocumentBaseName()}</span>
              </div>
            </div>
          </div>

          {/* Summary Header */}
          <AnalysisSummaryHeader 
            summaryData={analysisData}
            onRefresh={handleRefreshAnalysis}
            isLoading={isLoading}
            documentBaseName={getCurrentDocumentBaseName()}
          />

          {/* Filters */}
          <AnalysisFilters 
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            documentOptions={analysisData?.documentOptions}
            onBulkAction={handleBulkAction}
          />

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Main Analysis Panel */}
            <div className="xl:col-span-2">
              {/* Tab Navigation */}
              <AnalysisTabNavigation 
                activeTab={activeTab}
                onTabChange={setActiveTab}
                tabCounts={tabCounts}
              />

              {/* Results Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-medium text-foreground">
                    Analysis Results
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Showing {filteredFindings?.length} of {analysisData?.allFindings?.length} findings
                    {selectedDocumentBase !== 'all' && ` in ${getCurrentDocumentBaseName()}`}
                  </p>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowQueryPanel(!showQueryPanel)}
                  iconName="MessageSquare"
                  iconPosition="left"
                  iconSize={16}
                  className="xl:hidden"
                >
                  AI Query
                </Button>
              </div>

              {/* Findings List */}
              <div className="space-y-4">
                {filteredFindings?.length > 0 ? (
                  filteredFindings?.map((finding) => (
                    <AnalysisFindingCard
                      key={finding?.id}
                      finding={finding}
                      onAcceptSuggestion={handleAcceptSuggestion}
                      onCreateChangeRequest={handleCreateChangeRequest}
                      onNavigateToDocument={handleNavigateToDocument}
                    />
                  ))
                ) : (
                  <div className="text-center py-12 bg-card border border-border rounded-lg">
                    <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      {analysisData?.allFindings?.length === 0 ? 'No documents to analyze' : 'No findings match your criteria'}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {analysisData?.allFindings?.length === 0 
                        ? selectedDocumentBase === 'all' ?'Upload some documents to start generating analysis insights.'
                          : `No documents found in ${getCurrentDocumentBaseName()}. Upload documents to this document base to start analysis.`
                        : 'Try adjusting your filters or search terms to see more results.'
                      }
                    </p>
                    {analysisData?.allFindings?.length === 0 ? (
                      <Button
                        variant="outline"
                        onClick={() => navigate('/document-base-management')}
                        iconName="Upload"
                        iconPosition="left"
                        iconSize={16}
                      >
                        Upload Documents
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={handleClearFilters}
                        iconName="RotateCcw"
                        iconPosition="left"
                        iconSize={16}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Natural Language Query Panel */}
            <div className={`xl:block ${showQueryPanel ? 'block' : 'hidden'}`}>
              <NaturalLanguageQuery 
                onQuery={handleNaturalLanguageQuery}
                isLoading={isLoading}
                selectedDocumentBase={selectedDocumentBase}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AIAnalysisDashboard;